/**
 * Editor de una **tabla por calibre** de la Sección 3 (`precios_cabezal`,
 * `precios_jamba`, `precios_refuerzo`, `precios_division_6`, `precios_division_8`).
 *
 * Cada tabla es `{ calibre: [[hasta, precio], ...] }`. Columnas
 * `Calibre | Hasta (cm) | Precio`; el calibre es **solo-lectura** y, como el SSR,
 * **no** se permite añadir/quitar calibres ni filas aquí: solo editar las celdas
 * existentes. El ordenamiento por `hasta` se aplica al construir el body (no al
 * teclear). Presentacional: el estado lo posee `PreciosForm`.
 */
import {
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { TablaCalibreState, TablaRow } from "../precios.transform";

interface TablaCalibreEditorProps {
  /** Título de la tabla (p. ej. "Cabezal"). */
  title: string;
  /** Filas por calibre. */
  value: TablaCalibreState;
  onChange: (value: TablaCalibreState) => void;
}

export function TablaCalibreEditor({ title, value, onChange }: TablaCalibreEditorProps) {
  // Calibres en orden numérico ascendente para una presentación estable.
  const calibres = Object.keys(value).sort((a, b) => Number(a) - Number(b));

  function setCell(
    calibre: string,
    index: number,
    field: keyof TablaRow,
    cell: string,
  ) {
    const rows = value[calibre] ?? [];
    const nextRows = rows.map((row, i) =>
      i === index ? { ...row, [field]: cell } : row,
    );
    onChange({ ...value, [calibre]: nextRows });
  }

  const isEmpty = calibres.every((calibre) => (value[calibre] ?? []).length === 0);

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Calibre</TableHeaderCell>
            <TableHeaderCell>Hasta (cm)</TableHeaderCell>
            <TableHeaderCell>Precio</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isEmpty ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-slate-500">
                Sin datos.
              </TableCell>
            </TableRow>
          ) : (
            calibres.flatMap((calibre) =>
              (value[calibre] ?? []).map((row, index) => (
                <TableRow key={`${calibre}-${index}`}>
                  <TableCell className="font-medium text-slate-700">{calibre}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.1"
                      inputMode="decimal"
                      aria-label={`${title} calibre ${calibre} fila ${index + 1} hasta`}
                      value={row.hasta}
                      onChange={(event) =>
                        setCell(calibre, index, "hasta", event.target.value)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      aria-label={`${title} calibre ${calibre} fila ${index + 1} precio`}
                      value={row.precio}
                      onChange={(event) =>
                        setCell(calibre, index, "precio", event.target.value)
                      }
                    />
                  </TableCell>
                </TableRow>
              )),
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
}
